<template>
  <v-card>
    <v-card-title class="blue-grey lighten-5 text-primary">
      {{ tMilkTitle }}
    </v-card-title>

    <v-form ref="post_milk_form">

      <v-card-text class="py-3">

        <time-picker
          color="primary"
          base-color="primary"
          v-model="recordedAt"
          :label="tMilkTime"
          prepend-icon="mdi-clock-time-four-outline"
          :rules="[rules.required]"
          variant="outlined"
          density="compact"
        />

        <v-select
          color="primary"
          base-color="primary"
          v-model="milkCategory"
          :items="categories"
          item-title="title"
          item-value="value"
          :rules="[rules.required]"
          :label="tMilkType"
          variant="outlined"
          density="compact"
        ></v-select>

        <div class="d-flex align-baseline">
          <v-select
            color="primary"
            base-color="primary"
            v-model="amountId"
            :items="choices"
            item-title="title"
            item-value="id"
            :label="tMilkAmount"
            variant="outlined"
            density="compact"
          ></v-select>
          <p class="ml-1 body-1">cc</p>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          @click="close"
          color="error"
          variant="text"
        >
          {{ tCancel }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          @click="clickPost"
          :loading="btnLoading"
          color="primary"
          variant="flat"
        >
          {{ tSubmit }}
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>

    </v-form>

  </v-card>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { t } from '@/i18n';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');
import rules from '@/service/ValidRules';

import TimePicker from '@/components/molecules/TimePicker.vue';
import { dailyReportModule } from '@/store/entityModules/dailyReport';

// model
import { VForm } from '@/model/Vuetify/VForm';

interface DataType {
  recordedAt: string;
  amountId: number | null;
  milkCategory: string;
  btnLoading: boolean;
  isRecordetAtPicker: boolean;
}

const MilkCategory = {
  formula: 'formula', // ミルク
  breast: 'breast', // 母乳
};

export default defineComponent({
  name: 'MilkModal',
  components: {
    TimePicker,
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    childId: {
      type: Number,
      default: NaN,
    },
    selectedDate: {
      type: String,
      default: dayjs().format('YYYY-MM-DD'),
    },
  },

  computed: {
    tMilkTitle(): string {
      return t('record.modal.milkTitle', 'ミルク');
    },
    tMilkTime(): string {
      return t('record.modal.milkTime', '時間');
    },
    tMilkType(): string {
      return t('record.modal.milkType', '種類');
    },
    tMilkAmount(): string {
      return t('record.modal.milkAmount', '量');
    },
    tCancel(): string {
      return t('common.cancel', 'キャンセル');
    },
    tSubmit(): string {
      return t('common.submit', '送信');
    },
    rules() {
      return rules;
    },
    form(): VForm {
      return (this.$refs as any).post_milk_form;
    },
    categories() {
      return [
        {
          title: t('record.status.milkFormula', 'ミルク'),
          value: MilkCategory.formula,
        },
        {
          title: t('record.status.milkBreast', '母乳'),
          value: MilkCategory.breast,
        },
      ];
    },
    choices() {
      return dailyReportModule.milkChoices.map((item) => ({
        id: item.id,
        title: item.id === null
          ? t('common.unknown', '不明')
          : String(item.value),
      }));
    },
  },

  watch: {
    isOpen: {
      handler(val: boolean) {
        if (val) {
          this.recordedAt = dayjs().format('HH:00');
          this.amountId = null;
          if (this.choices.length < 1) {
            dailyReportModule.fetchMilkChoices();
          }
        }
      },
      deep: true,
    },
  },

  data: (): DataType =>({
    recordedAt: dayjs().format('HH:00'),
    amountId: 10,
    milkCategory: 'formula',
    btnLoading: false,
    isRecordetAtPicker: false,
  }),

  methods: {
    allowedStep: (m) => m % 5 === 0,
    close() {
      this.$emit('close');
    },
    async clickPost() {
      if (this.form.validate() && this.milkCategory) {
        this.btnLoading = true;
        try {
          const recordedAtHour = this.recordedAt.split(':')[0];
          const recordedAtMinutes = this.recordedAt.split(':')[1];
          const recordedAtTime = dayjs().hour(Number(recordedAtHour)).minute(Number(recordedAtMinutes));
          await dailyReportModule.postMilk({
            childId: this.childId,
            date: this.selectedDate,
            recordedAt: dayjs(recordedAtTime).toISOString(),
            amountId: this.amountId,
            milkCategory: this.milkCategory,
          });
          this.$emit('posted');
          this.$emit('close');
          this.btnLoading = false;
        } catch (error) {
          this.$emit('close');
          this.btnLoading = false;
        }
      }
    },
  },

  mounted() {
    dailyReportModule.fetchMilkChoices();
  },
});
</script>
<style lang="scss" scoped>
</style>
